import { useState, useEffect } from "react";
import { apiFetch } from "../api";
import { trackEvent } from "../analytics/posthog";
import s from "./PremiumModal.module.css";

export default function PremiumModal({ onClose, userEmail }) {
  const [isPremium, setIsPremium] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState("info"); // "info" | "waitlist" | "verify_sent" | "joined"

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await apiFetch("/api/v1/premium/pricing");
        if (!res.ok) throw new Error("Failed to load pricing");
        const data = await res.json();
        if (!cancelled) {
          setIsPremium(data.is_premium);
          setEmailVerified(data.email_verified);
          if (data.on_waiting_list) setStep("joined");
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  async function handlePay() {
    trackEvent("premium_cta_clicked");
    setStep("waitlist");
  }

  async function handleJoinWaitingList() {
    setError("");
    setSubmitting(true);
    try {
      const res = await apiFetch("/api/v1/premium/join-waiting-list", {
        method: "POST",
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.detail || "Failed to join waiting list.");
      trackEvent("waiting_list_joined");
      setStep("joined");
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSendVerification() {
    setError("");
    setSubmitting(true);
    try {
      const res = await apiFetch("/api/v1/premium/send-verification", {
        method: "POST",
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.detail || "Failed to send verification email.");
      setStep("verify_sent");
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  const CheckIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg>
  );

  const SuccessIcon = () => (
    <div className={s.premiumSuccessIcon}>
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2a6e3e" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg>
    </div>
  );

  return (
    <div className={s.dialogBackdrop} onClick={onClose}>
      <div className={s.premiumModal} onClick={(e) => e.stopPropagation()}>
        <div className={s.premiumIcon}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        </div>
        <h3 className={s.premiumTitle}>Upgrade to Premium</h3>
        <p className={s.premiumDesc}>Unlock your full Swedish learning journey</p>

        <ul className={s.premiumBenefits}>
          <li><CheckIcon /> Unlimited access to all lessons</li>
          <li><CheckIcon /> All future lessons included</li>
          <li><CheckIcon /> 100 daily AI Tutor messages</li>
          <li><CheckIcon /> Full audio downloads for offline learning</li>
        </ul>

        {loading ? (
          <div className={s.premiumCta}><p className={s.premiumCtaText}>Loading plans...</p></div>
        ) : isPremium ? (
          <div className={s.premiumCta}>
            <p className={s.premiumCtaText}>You already have an active premium subscription!</p>
          </div>
        ) : step === "joined" ? (
          <div className={s.premiumCta}>
            <SuccessIcon />
            <p className={s.premiumCtaText} style={{ color: "#2a6e3e", fontWeight: 700 }}>
              You're on the waiting list!
            </p>
            <p className={s.premiumCtaText}>
              We'll email you when Premium launches. As a waiting list member, you'll get <strong style={{ color: "#d4a843" }}>50% off</strong> — just <strong><s>99 SEK</s> 49 SEK</strong> for your first month!
            </p>
          </div>
        ) : step === "verify_sent" ? (
          <div className={s.premiumCta}>
            <div className={s.premiumSuccessIcon}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#d4a843" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
              </svg>
            </div>
            <p className={s.premiumCtaText} style={{ color: "#2a6e3e", fontWeight: 700 }}>
              Check your inbox!
            </p>
            <p className={s.premiumCtaText}>
              We sent a verification link to <strong>{userEmail}</strong>. Click it to verify your email and join the waiting list.
            </p>
          </div>
        ) : step === "waitlist" ? (
          <>
            {emailVerified ? (
              <>
                <div className={s.waitlistCard}>
                  <div className={s.waitlistBadge}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 8v4l3 3" />
                      <circle cx="12" cy="12" r="10" />
                    </svg>
                    Coming Soon
                  </div>
                  <p className={s.premiumCtaText}>
                    Premium is launching soon! Join the waiting list and be the first to know.
                  </p>
                  <div className={s.discountHighlight}>
                    <span className={s.discountLabel}>Waiting list exclusive</span>
                    <span className={s.discountValue}>50% off</span>
                    <span className={s.discountDetail}><s>99 SEK</s> → 49 SEK / month</span>
                  </div>
                </div>

                {error && <p className={s.premiumError}>{error}</p>}

                <div className={s.premiumActions}>
                  <button className={s.dialogCancel} onClick={() => setStep("info")}>Back</button>
                  <button
                    className={s.premiumRequestBtn}
                    disabled={submitting}
                    onClick={handleJoinWaitingList}
                  >
                    {submitting ? "Joining..." : "Join Waiting List"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className={s.waitlistCard}>
                  <div className={s.waitlistBadge}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 8v4l3 3" />
                      <circle cx="12" cy="12" r="10" />
                    </svg>
                    Coming Soon
                  </div>
                  <p className={s.premiumCtaText}>
                    Premium is launching soon! Join the waiting list and get <strong style={{ color: "#d4a843" }}>50% off</strong> when it launches.
                  </p>
                  <div className={s.discountHighlight}>
                    <span className={s.discountLabel}>Waiting list exclusive</span>
                    <span className={s.discountValue}>50% off</span>
                    <span className={s.discountDetail}><s>99 SEK</s> → 49 SEK / month</span>
                  </div>
                  <div className={s.verifyNote}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span>To join the waiting list, we need to verify your email address first.</span>
                  </div>
                </div>

                {error && <p className={s.premiumError}>{error}</p>}

                <div className={s.premiumActions}>
                  <button className={s.dialogCancel} onClick={() => setStep("info")}>Back</button>
                  <button
                    className={s.premiumRequestBtn}
                    disabled={submitting}
                    onClick={handleSendVerification}
                  >
                    {submitting ? "Sending..." : "Verify My Email"}
                  </button>
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <div className={s.planSingle}>
              <div className={s.planLabel}>1 Month</div>
              <div className={s.planPrice}>99 <span className={s.planCurrency}>SEK</span></div>
            </div>

            {error && <p className={s.premiumError}>{error}</p>}

            <div className={s.premiumActions}>
              <button className={s.dialogCancel} onClick={onClose}>Maybe Later</button>
              <button
                className={s.premiumRequestBtn}
                onClick={handlePay}
              >
                Pay
              </button>
            </div>
          </>
        )}

        {(isPremium || step === "joined" || step === "verify_sent") && (
          <div className={s.premiumActions}>
            <button className={s.dialogCancel} onClick={onClose}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
}
